/**
 * Views Page
 * 
 * Main Views page that handles routing between different Views functionality:
 * - List/manage existing views
 * - Create new views
 * - Edit existing views
 * - View individual views in preview mode
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  TextSnippet as TextSnippetIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { ledeSx } from '../theme/styleHelpers';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import {
  ViewManager,
  ViewPreview,
} from '../components/views';
import { PageBuilderWrapper } from '../components/page-builder/PageBuilderWrapper';
import { View } from '../types/view.types';
import { viewsApiService } from '../services/views.api';
import { contentService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import ShareDialog from '../components/shared/ShareDialog';
import { ContentMetadataDialog } from '../components/content/ContentMetadataDialog';
import { RateViewDialog } from '../components/views/RateViewDialog';

interface ViewsPageProps {}

const ViewsPage: React.FC<ViewsPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { organizations, teams, currentOrganizationId, currentTeamId } = useOrganization();

  const [currentView, setCurrentView] = useState<View | null>(null);
  const [loading, setLoading] = useState(false);

  // Check permissions
  const { hasPermission } = usePermissions();
  const canCreateView = hasPermission(PERMISSIONS.VIEW_CREATE);
  const canUpdateView = hasPermission(PERMISSIONS.VIEW_UPDATE);


  const handleViewSelect = (view: View) => {
    setCurrentView(view);
    navigate(`/views/${view.id}`);
  };

  const handleViewEdit = (view: View) => {
    if (!canUpdateView) {
      return;
    }
    setCurrentView(view);
    navigate(`/views/${view.id}/edit`);
  };

  const handleViewCreate = () => {
    if (!canCreateView) {
      return;
    }
    navigate('/views/create');
  };

  const handleViewSave = (view: View) => {
    setCurrentView(view);
    navigate(`/views/${view.id}`);
  };

  const handleCancel = () => {
    navigate('/views');
  };

  // Get team context - Views are team-specific only
  const currentTeam = teams.find(team => team.id === currentTeamId);
  const teamId = currentTeam?.id;
  const organizationId = currentTeam?.organizationId;

  if (!teamId) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          Please select a team to manage views.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Views are used to create curated content experiences for your team.
        </Typography>
      </Box>
    );
  }

  // Check if we're on an editor route (create or edit)
  const isEditorRoute = location.pathname.includes('/create') || location.pathname.includes('/edit');

  // Suppress the page-level "Views" chrome on the renderer route itself
  // (/views/:viewId without /edit). The renderer gets its own per-view header.
  // Match /views/<some-id> where <some-id> is not 'create' and the path doesn't end with /edit.
  const isRendererRoute =
    /^\/views\/[^/]+$/.test(location.pathname) &&
    !location.pathname.endsWith('/edit') &&
    !location.pathname.endsWith('/create');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Page header for list route only (not editor, not renderer) */}
      {!isEditorRoute && !isRendererRoute && (
        <Container maxWidth="xl" sx={{ py: 3, pb: 0 }}>
          <Typography variant="h1" sx={{
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            marginBottom: 1,
          }}>
            Views
          </Typography>
          <Typography sx={ledeSx}>
            Create and edit custom views for content.
          </Typography>
        </Container>
      )}

      {/* Main content area - full width for editor, constrained for other views */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Routes>
          {/* Views list/management */}
          <Route
            index
            element={
              <Container maxWidth="xl">
                <ViewManager
                  organizationId={organizationId}
                  teamId={teamId}
                  onViewSelect={handleViewSelect}
                  onViewEdit={handleViewEdit}
                  onViewCreate={handleViewCreate}
                  mode="manage"
                />
              </Container>
            }
          />

          {/* Create new view */}
          <Route
            path="create"
            element={
              <PageBuilderWrapper
                mode="create"
                organizationId={organizationId}
                teamId={teamId}
                onClose={handleCancel}
              />
            }
          />

          {/* View individual view */}
          <Route
            path=":viewId"
            element={<ViewDetailPage onEdit={handleViewEdit} />}
          />

          {/* Edit view */}
          <Route
            path=":viewId/edit"
            element={
              <ViewEditorPage
                organizationId={organizationId}
                teamId={teamId}
                onSave={handleViewSave}
                onCancel={handleCancel}
              />
            }
          />
        </Routes>
      </Box>
    </Box>
  );
};

// Status string → ItemStatus number mapping
const STATUS_NUMBER: Record<string, number> = {
  draft: 1,
  private: 2,
  public: 3,
  archived: 4,
};

// View detail page component
const ViewDetailPage: React.FC<{ onEdit: (view: View) => void }> = ({ onEdit }) => {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { teams, currentTeamId } = useOrganization();
  const [view, setView] = useState<View | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);

  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState<string | undefined>(undefined);
  const [bookmarkBusy, setBookmarkBusy] = useState(false);

  const { hasPermission } = usePermissions();
  const canEditPayload = hasPermission(PERMISSIONS.VIEW_UPDATE);
  const canEditMetadata = hasPermission(PERMISSIONS.CONTENT_UPDATE);
  // TODO (Plan C1 follow-up): once the View type/backend transformer support
  // view.permissions.canShare, restore the per-item check:
  //   const canShare = hasPermission(PERMISSIONS.CONTENT_SHARE)
  //                    && (view?.permissions?.canShare ?? true);
  // Currently the backend doesn't populate this on view responses, so the
  // per-item flag defaults to true and the role gate is the only filter.
  const canShare = hasPermission(PERMISSIONS.CONTENT_SHARE);
  const canRate = hasPermission(PERMISSIONS.CONTENT_COMMENT);
  const currentUserId = user?._id ?? user?.id ?? '';

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const currentTeam = teams.find(team => team.id === currentTeamId);
  const teamId = currentTeam?.id;
  const organizationId = currentTeam?.organizationId;

  useEffect(() => {
    if (viewId) {
      loadView();
    }
  }, [viewId]);

  const loadView = async () => {
    if (!viewId) return;

    setLoading(true);
    try {
      const loadedView = await viewsApiService.getView(viewId, teamId!);
      setView(loadedView);

      // Fetch bookmark status (non-fatal)
      try {
        const status = await contentService.isContentBookmarked(viewId);
        setBookmarked(status.is_bookmarked);
        setBookmarkId(status.bookmark_id);
      } catch (err) {
        // Non-fatal: assume not bookmarked
        setBookmarked(false);
        setBookmarkId(undefined);
      }
    } catch (err) {
      console.error('Error loading view:', err);
      setError('Failed to load view');
    } finally {
      setLoading(false);
    }
  };

  const handleBookmarkToggle = async () => {
    if (!viewId || !teamId || bookmarkBusy) return;

    const prevBookmarked = bookmarked;
    const prevBookmarkId = bookmarkId;

    // Optimistic UI update
    setBookmarkBusy(true);
    setBookmarked(!prevBookmarked);

    try {
      if (prevBookmarked && prevBookmarkId) {
        await contentService.deleteBookmark(prevBookmarkId);
        setBookmarkId(undefined);
      } else {
        const result = await contentService.createBookmark({
          content_id: viewId,
          team_id: teamId,
          organization_id: organizationId,
        });
        setBookmarkId(result.bookmark_id);
      }
    } catch (err) {
      // Revert on failure
      console.error('Bookmark toggle failed:', err);
      setBookmarked(prevBookmarked);
      setBookmarkId(prevBookmarkId);
    } finally {
      setBookmarkBusy(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading view...</Typography>
      </Box>
    );
  }

  if (error || !view) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          {error || 'View not found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Per-view reading header — title + description + kebab. */}
      <Container maxWidth="lg" sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2, mb: view.description ? 1 : 0 }}
              noWrap
              title={view.name}
            >
              {view.name}
            </Typography>
            {view.description && (
              <Typography variant="body2" color="text.secondary">
                {view.description}
              </Typography>
            )}
          </Box>

          {/* Kebab — Bookmark is always available, so this renders for any
              authenticated team member once a view is loaded. */}
          <IconButton
            aria-label="View actions"
            onClick={handleMenuOpen}
            size="small"
            sx={{ mt: 0.5 }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Container>

      {/* Renderer body */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {view.pageDoc ? (
          <PageBuilderWrapper
            mode="view"
            viewId={viewId}
            organizationId={organizationId!}
            teamId={teamId!}
          />
        ) : (
          <Container maxWidth="lg">
            <ViewPreview
              viewId={viewId}
              mdxContent={view.mdxContent || ''}
              viewData={view}
              organizationId={organizationId!}
              teamId={teamId!}
              onError={(error) => setError(error.message)}
            />
          </Container>
        )}
      </Box>

      {/* Kebab Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        {canEditPayload && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate(`/views/${viewId}/edit`);
            }}
          >
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canEditMetadata && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setMetadataDialogOpen(true);
            }}
          >
            <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit metadata</ListItemText>
          </MenuItem>
        )}
        {canShare && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setShareDialogOpen(true);
            }}
          >
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
        )}
        {canRate && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setRateDialogOpen(true);
            }}
          >
            <ListItemIcon><StarIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rate</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleBookmarkToggle();
          }}
          disabled={bookmarkBusy}
        >
          <ListItemIcon>
            {bookmarked ? <BookmarkIcon fontSize="small" color="primary" /> : <BookmarkBorderIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{bookmarked ? 'Remove bookmark' : 'Bookmark'}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Metadata Dialog */}
      {view && (
        <ContentMetadataDialog
          open={metadataDialogOpen}
          onClose={() => setMetadataDialogOpen(false)}
          onSave={async (values) => {
            // Map string status → ItemStatus number for UpdateViewRequest
            const statusNumber = values.status ? STATUS_NUMBER[values.status] : undefined;
            await viewsApiService.updateView(viewId!, {
              name: values.title,
              description: values.description,
              status: statusNumber,
              tagIds: values.tagIds,
            }, teamId!);
            setMetadataDialogOpen(false);
            await loadView();
          }}
          initialValues={{
            title: view.name,
            description: view.description,
            status: (view.status as 'draft' | 'private' | 'public' | 'archived') ?? 'draft',
            tagIds: view.tagIds ?? [],
            thumbnailUrl: view.thumbnail,
            teamId: view.teamId,
            organizationId: view.organizationId,
          }}
          kind="view"
          contentId={viewId}
          availableStatuses={['draft', 'private', 'public', 'archived']}
        />
      )}

      {/* Share Dialog */}
      {view && shareDialogOpen && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          itemType="view"
          itemId={viewId!}
          itemTitle={view.name}
        />
      )}

      {/* Rate Dialog */}
      {view && (
        <RateViewDialog
          open={rateDialogOpen}
          onClose={() => setRateDialogOpen(false)}
          viewId={viewId!}
          teamId={teamId!}
          viewName={view.name}
          currentUserId={currentUserId}
          onSubmitted={(avg, total) => {
            // Update the loaded view's rating fields so any header indicators
            // reflect the new aggregate immediately (no full reload needed).
            setView((prev) =>
              prev
                ? {
                    ...prev,
                    avgRating: avg ?? (prev as any).avgRating,
                    ratingCount: total ?? (prev as any).ratingCount,
                  }
                : prev,
            );
          }}
        />
      )}
    </Box>
  );
};

// View editor page component
const ViewEditorPage: React.FC<{
  organizationId: string;
  teamId: string;
  onSave: (view: View) => void;
  onCancel: () => void;
}> = ({ organizationId, teamId, onSave, onCancel }) => {
  const { viewId } = useParams<{ viewId: string }>();
  const [view, setView] = useState<View | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (viewId) {
      loadView();
    }
  }, [viewId]);

  const loadView = async () => {
    if (!viewId) return;
    
    setLoading(true);
    try {
      const loadedView = await viewsApiService.getView(viewId, teamId!);
      setView(loadedView);
    } catch (err) {
      console.error('Error loading view:', err);
      setError('Failed to load view');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading view for editing...</Typography>
      </Box>
    );
  }

  if (error || !view) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          {error || 'View not found'}
        </Typography>
      </Box>
    );
  }

  return (
    <PageBuilderWrapper
      mode="edit"
      viewId={viewId}
      organizationId={organizationId}
      teamId={teamId}
      onClose={onCancel}
    />
  );
};

export default ViewsPage;